import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Download, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/i18n/LocaleContext';
import { SectionHeading } from './SectionHeading';

const TEMPLATES = [
  { file: '/assets/02-memes/night-market-square-1200.png', idx: 0, rotate: '-rotate-2', plate: 'bg-primary text-primary-foreground' },
  { file: '/assets/02-memes/lantern-signal-square-1200.png', idx: 1, rotate: 'rotate-1', plate: 'bg-accent text-accent-foreground' },
  { file: '/assets/02-memes/dip-template-1200.png', idx: 2, rotate: 'rotate-2', plate: 'bg-card text-foreground' },
  { file: '/assets/02-memes/celebrate-template-1200.png', idx: 3, rotate: '-rotate-1', plate: 'bg-primary text-primary-foreground' },
  { file: '/assets/02-memes/tea-template-1200.png', idx: 4, rotate: 'rotate-1', plate: 'bg-gold text-ink' },
];

const MemeCard = ({ tpl, caption, alt, downloadLabel, delay, reduced }) => (
  <motion.figure
    initial={reduced ? {} : { opacity: 0, y: 28 }}
    whileInView={reduced ? {} : { opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`gallery-item-${tpl.idx}`}
    className={`group relative sticker-frame rounded-2xl overflow-hidden bg-card ${tpl.rotate} hover:rotate-0 transition-transform duration-300 flex flex-col`}
  >
    <div className="relative overflow-hidden">
      <img
        src={tpl.file}
        alt={alt}
        className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-[1.05]"
        loading="lazy"
      />
      <a
        href={tpl.file}
        download
        data-testid={`gallery-download-${tpl.idx}`}
        aria-label={downloadLabel}
        className="absolute top-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border-2 border-ink bg-primary text-primary-foreground opacity-90 hover:opacity-100 shadow-[3px_3px_0_hsl(var(--ink))]"
      >
        <Download className="h-4 w-4" aria-hidden="true" />
      </a>
    </div>
    <figcaption className={`mt-auto border-t-[3px] border-ink px-5 py-4 ${tpl.plate}`}>
      <p className="font-display text-lg sm:text-xl font-bold leading-snug">{caption}</p>
    </figcaption>
  </motion.figure>
);

export const Gallery = () => {
  const { t } = useLocale();
  const reduced = useReducedMotion();
  const g = t.gallery;

  return (
    <section id="memes" data-testid="gallery-section" className="relative py-20 sm:py-28 border-t-2 border-border overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'url(/assets/04-backgrounds/red-lantern-field.svg)', backgroundSize: '440px' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeading testId="gallery-heading" label={g.label} title={g.title} intro={g.intro} />
          <img
            src="/assets/01-mascot/cutouts/peipei-celebrate-flat.png"
            alt=""
            aria-hidden="true"
            className="hidden lg:block w-40 rotate-[6deg] drop-shadow-xl shrink-0"
            loading="lazy"
          />
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {TEMPLATES.map((tpl, i) => (
            <MemeCard
              key={tpl.file}
              tpl={tpl}
              caption={g.captions[tpl.idx]}
              alt={`${g.altPrefix} ${g.items[tpl.idx]}`}
              downloadLabel={g.download}
              delay={i * 0.05}
              reduced={reduced}
            />
          ))}

          {/* Motion loop, honors prefers-reduced-motion with a static fallback */}
          <motion.figure
            initial={reduced ? {} : { opacity: 0, y: 28 }}
            whileInView={reduced ? {} : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="gallery-motion-card"
            className="group relative sticker-frame rounded-2xl overflow-hidden bg-card -rotate-1 hover:rotate-0 transition-transform duration-300 flex flex-col"
          >
            <div className="relative overflow-hidden">
              {reduced ? (
                <img
                  src="/assets/02-memes/celebrate-template-1200.png"
                  alt={`${g.altPrefix} ${g.items[3]}`}
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                />
              ) : (
                <video
                  className="w-full aspect-square object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/assets/02-memes/celebrate-template-1200.png"
                  aria-label={`${g.altPrefix} ${g.motionTitle}`}
                >
                  <source src="/assets/07-motion/peipei-celebrate-loop-1080.mp4" type="video/mp4" />
                </video>
              )}
              <a
                href="/assets/07-motion/peipei-celebrate-loop.gif"
                download
                data-testid="gallery-download-motion"
                aria-label="GIF"
                className="absolute top-3 right-3 flex h-10 items-center gap-2 px-3 rounded-full border-2 border-ink bg-primary text-primary-foreground opacity-90 hover:opacity-100 shadow-[3px_3px_0_hsl(var(--ink))]"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                <span className="text-xs font-bold">GIF</span>
              </a>
            </div>
            <figcaption className="mt-auto border-t-[3px] border-ink px-5 py-4 bg-accent text-accent-foreground">
              <p className="font-display text-lg sm:text-xl font-bold leading-snug flex items-center gap-2">
                <Film className="h-5 w-5 shrink-0" aria-hidden="true" />
                {g.motionTitle}
              </p>
              <p className="mt-1 text-xs opacity-80">{g.motionNote}</p>
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
};
